import { useMemo } from "react";

import type { ChartMentions } from "@app/component/EntityMentions";
import { hasChartMention, useResolvedChartMentions } from "@app/component/EntityMentions";
import MmcNotification from "@app/types/MmcNotification";

// A chart marker can ride in a coaching note or in a coaching task's note.
const mentionsChart = ({ coachingNote, coachingTask }: MmcNotification): boolean =>
  hasChartMention(coachingNote?.note ?? "") || hasChartMention(coachingTask?.note ?? "");

/**
 * Chart pills for the notification panel: resolved only when a notification on
 * screen actually carries a marker, and closing the panel on the way out —
 * opening a chart navigates out from under it, and nothing else dismisses it.
 */
const useNotificationChartMentions = (
  notifications: MmcNotification[],
  onHide: () => void
): ChartMentions => {
  const needsChartNames = useMemo(() => notifications.some(mentionsChart), [notifications]);
  const { loading, names, onOpen } = useResolvedChartMentions(needsChartNames);

  return useMemo<ChartMentions>(
    () => ({
      loading,
      names,
      onOpen: (chartId) => {
        onHide();
        onOpen?.(chartId);
      },
    }),
    [loading, names, onHide, onOpen]
  );
};

export default useNotificationChartMentions;
